import { useEffect, useMemo, useState } from 'react'
import { Input } from '../../../components/ui/input'
import { UserService } from '../../../services/UserService'
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/Avatar'
import { Button } from '../../../components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover'

export type AdminUser = {
  id: string
  name: string
  email?: string
  avatar?: string
}

interface UsersListProps {
  onSelect: (user: AdminUser) => void
  selectedUserId?: string | null
}

export function UsersList({ onSelect, selectedUserId }: UsersListProps) {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)
  const [total, setTotal] = useState(0)
  const [rawOpenFor, setRawOpenFor] = useState<string | null>(null)
  const [rawData, setRawData] = useState<Record<string, any>>({})

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const offset = (page - 1) * pageSize
        const hasQuery = !!filter.trim()
        if (hasQuery) {
          const { data, count } = await UserService.searchUsersWithCount(filter.trim(), pageSize, offset)
          const mapped = (data || []).map((u: any) => ({
            id: u.id,
            name: u.full_name || u.email || 'User',
            email: u.email || undefined,
            avatar: u.avatar_url || undefined,
          }))
          setUsers(mapped)
          setTotal(count || 0)
        } else {
          const { data, count } = await UserService.listUsersWithCount(pageSize, offset)
          const mapped = (data || []).map((u: any) => ({
            id: u.id,
            name: u.full_name || u.email || 'User',
            email: u.email || undefined,
            avatar: u.avatar_url || undefined,
          }))
          setUsers(mapped)
          setTotal(count || 0)
        }
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [page, pageSize, filter])

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((total || 0) / pageSize))
  }, [total, pageSize])

  const openRawForUser = async (userId: string) => {
    setRawOpenFor(prev => (prev === userId ? null : userId))
    if (!rawData[userId]) {
      const { data } = await UserService.getProfileRaw(userId)
      setRawData(prev => ({ ...prev, [userId]: data }))
    }
  }

  return (
    <div className="border rounded-md p-3 bg-white">
      <div className="mb-2 flex items-center gap-2">
        <Input
          placeholder="Search by name or email"
          value={filter}
          onChange={(e) => { setPage(1); setFilter(e.target.value) }}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
        <div>
          {loading ? 'Loading users…' : `${total} users`}
        </div>
        <div className="flex items-center gap-2">
          <span>Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</Button>
        </div>
      </div>
      <div className="space-y-2 max-h-[60vh] overflow-auto">
        {users.map(u => (
          <div key={u.id} className={`w-full px-3 py-2 rounded-md border ${selectedUserId === u.id ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'}`}>
            <div className="flex items-center gap-2">
              <button onClick={() => onSelect(u)} className="flex items-center gap-2 flex-1 text-left">
                <Avatar className="h-6 w-6">
                  {u.avatar ? (
                    <AvatarImage
                      src={u.avatar}
                      alt={u.name}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  ) : null}
                  <AvatarFallback>
                    {(u.name || u.email || 'U').trim().charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="text-sm font-medium">{u.name}</div>
                  <div className="text-xs text-gray-500">{u.email}</div>
                </div>
              </button>
              <Popover open={rawOpenFor === u.id} onOpenChange={() => openRawForUser(u.id)}>
                <PopoverTrigger asChild>
                  <Button size="sm" variant="outline">Raw data</Button>
                </PopoverTrigger>
                <PopoverContent className="p-2 w-[28rem] max-w-[90vw]">
                  <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium">Raw profile</div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => {
                        const text = JSON.stringify(rawData[u.id] ?? {}, null, 2)
                        navigator.clipboard.writeText(text)
                      }}>Copy</Button>
                      <Button size="sm" variant="ghost" onClick={() => window.open(`/api/admin/profile/${u.id}`, '_blank')}>Open</Button>
                    </div>
                  </div>
                  <pre className="text-[11px] whitespace-pre-wrap break-words max-h-64 overflow-auto bg-gray-50 p-2 rounded">
{JSON.stringify(rawData[u.id] ?? {}, null, 2)}
                  </pre>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        ))}
        {!loading && users.length === 0 && (
          <div className="text-xs text-gray-500">No results</div>
        )}
      </div>
    </div>
  )
}


